/* eslint-disable react-hooks/exhaustive-deps */
import { FormEvent, useEffect, useMemo, useRef, useState } from "react";
import { Box, Button, Container, Grid, Link, Typography, useMediaQuery, useTheme } from "@mui/material";
import {
  AccountBalanceOutlined,
  CheckCircle,
  RadioButtonUnchecked,
  WarningAmber as WarningAmberIcon,
  WorkOutline,
} from "@mui/icons-material";
import { useSelector } from "react-redux";
import { useNavigate } from "react-router-dom";
import * as Sentry from "@sentry/react";
import * as yup from "yup";
import { useFormik } from "formik";
import { useLazyGetTabsConfigQuery, useLazyGetBanksByCodeQuery, useLazyGetTaxSystemsQuery } from "@api/catalogs";
import {
  useDeleteBankAccountMutation,
  useDeleteBankAccountStatementMutation,
  useLazyGetBankAccountsQuery,
  usePostBankAccountsMutation,
  usePostBankAccountStatementMutation,
  usePostValidateNextStageMutation,
  usePutApplicationCompletedMutation,
} from "@api/applications";
import { useLazyGetDocumentAppIdQuery, usePatchDocumentMutation } from "@api/documents";
import { InactivityHandler } from "@components/InactivityHandler";
import { ProgressBar } from "@components/ProgressBar";
import CustomRightImage from "@components/CustomRightImagev2";
import CustomUploadFileV2 from "@components/CustomUploadFileV2";
import { CustomDialog } from "@components/CustomDialog";
import {
  BOFU_TABS_CONFIG,
  DEFAULT_NUMBER_BANK_STATEMENTS,
  DOCUMENTS_CATALOG,
  STATUS_CATALOG,
  TAX_SYSTEM_TYPE,
} from "@helpers/constantes";
import { getBankInformationConfig, getBankStatementIndex, identifyBankAccounts } from "@helpers/funciones";
import useExpedienteDocuments from "@hooks/useExpedienteDocuments";
import useStatusConfig from "@hooks/useStatusConfig";
import useTofuSteps from "@hooks/useTofuSteps";
import {
  BankAccountFormItem,
  BofuTabDocument,
  DocumentResponse,
  PayloadBankAccount,
  TabsConfigResponseData,
} from "@interfaces/index";
import { ApiError } from "@interfaces/response";
import { setModalErrorVisible } from "@store/slices/appSlice";
import { setLoanStatus } from "@store/slices/registerSlice";
import { RootState, useAppDispatch } from "@store/store";
import { UUID } from "node:crypto";
import {
  generateDocumentConfig,
  validateDocumentStructure,
  validateRequiredDocumentsAreUploaded,
} from "@components/TabBofu/functions";
import DocumentsTabBofu from "@components/TabBofu/DocumentsTabBofu";
import { CiecValidatorRequest, useCiecValidatorMutation } from "@api/sat_apis_new/satApsi";

const BANKCODE = "999";
const SCREEN_STATUS = "CSDOS";

type DocumentType = "financieros" | "actividad";

const createDefaultBankAccount = (
  numberOfBankStatements: number,
  bankId: string,
  bankName: string,
): BankAccountFormItem => ({
  id: "",
  clabe: "000000000000000000",
  bank_id: bankId,
  bank_name: bankName,
  statements: Array.from({ length: numberOfBankStatements }, (_, index) => ({
    bank_statement_document_id: undefined,
    document_id: "",
    document_order: index + 1,
    document_value: undefined,
  })),
});

const normalizeStatements = (
  statements: Array<BankAccountFormItem["statements"][number] | undefined> | undefined,
  targetCount: number,
): BankAccountFormItem["statements"] => {
  const current = Array.isArray(statements) ? [...statements] : [];
  return Array.from({ length: targetCount }, (_, index) => {
    const existing = current[index];
    if (existing) {
      return {
        bank_statement_document_id: existing.bank_statement_document_id,
        document_id: existing.document_id ?? "",
        document_order: index + 1,
        document_value: existing.document_value,
      };
    }
    return {
      bank_statement_document_id: undefined,
      document_id: "",
      document_order: index + 1,
      document_value: undefined,
    };
  });
};

const DOCUMENT_TYPE_OPTIONS: Array<{
  value: DocumentType;
  title: string;
  description: string;
  icon: typeof AccountBalanceOutlined;
}> = [
  {
    value: "financieros",
    title: "Documentos financieros",
    description: "Estados financieros, estados de cuenta y documentos SAT.",
    icon: AccountBalanceOutlined,
  },
  {
    value: "actividad",
    title: "Documentos de actividad",
    description: "Comprobantes de operación o actividad de tu empresa.",
    icon: WorkOutline,
  },
];

const mapTabDocuments = ({
  tab,
  docsAccount,
  clientDetailsId,
}: {
  tab: TabsConfigResponseData | undefined;
  docsAccount: DocumentResponse[];
  clientDetailsId: UUID;
}): BofuTabDocument[] => {
  if (!tab?.documents?.length) {
    return [];
  }

  return tab.documents
    .filter((item) => validateDocumentStructure(item))
    .map((docItem) =>
      generateDocumentConfig({
        docItem,
        docs_account: docsAccount,
        docs_aval: [],
        clientDetailsId,
        aval: undefined,
      }),
    );
};

interface DocumentTypeOptionCardProps {
  title: string;
  description: string;
  icon: typeof AccountBalanceOutlined;
  isSelected: boolean;
  isMobileOrTablet: boolean;
  onSelect: () => void;
}

const DocumentTypeOptionCard = ({
  title,
  description,
  icon: Icon,
  isSelected,
  isMobileOrTablet,
  onSelect,
}: DocumentTypeOptionCardProps) => {
  return (
    <Box
      onClick={onSelect}
      onKeyDown={(event) => {
        if (event.key === "Enter" || event.key === " ") {
          event.preventDefault();
          onSelect();
        }
      }}
      role="button"
      tabIndex={0}
      sx={{
        p: isMobileOrTablet ? 2 : 2.5,
        border: "1px solid",
        borderColor: isSelected ? "primary.main" : "grey.300",
        borderRadius: 3,
        cursor: "pointer",
        backgroundColor: isSelected ? "rgba(82, 140, 214, 0.06)" : "common.white",
        boxShadow: isSelected ? "0px 0px 0px 1px rgba(82, 140, 214, 0.12)" : "none",
        transition: "border-color 0.2s ease, background-color 0.2s ease, box-shadow 0.2s ease",
        "&:hover": {
          borderColor: "primary.main",
        },
      }}
    >
      <Box display="flex" alignItems="flex-start" gap={2}>
        <Box
          sx={{
            width: isMobileOrTablet ? 40 : 44,
            height: isMobileOrTablet ? 40 : 44,
            minWidth: isMobileOrTablet ? 40 : 44,
            borderRadius: 1.5,
            backgroundColor: isSelected ? "primary.main" : "grey.100",
            color: isSelected ? "common.white" : "primary.dark",
            display: "flex",
            alignItems: "center",
            justifyContent: "center",
          }}
        >
          <Icon fontSize="small" />
        </Box>

        <Box flex={1}>
          <Box
            display="flex"
            alignItems={isMobileOrTablet ? "flex-start" : "center"}
            justifyContent="space-between"
            gap={1.5}
            flexDirection={isMobileOrTablet ? "column" : "row"}
          >
            <Typography variant={isMobileOrTablet ? "body1" : "h6"} fontWeight={600} color="primary.dark">
              {title}
            </Typography>

            {isSelected ? (
              <CheckCircle sx={{ color: "primary.main", fontSize: 26 }} />
            ) : (
              <RadioButtonUnchecked sx={{ color: "grey.400", fontSize: 26 }} />
            )}
          </Box>

          <Typography
            variant={isMobileOrTablet ? "body2" : "body1"}
            mt={1}
            color="text.secondary"
            sx={{ lineHeight: 1.5 }}
          >
            {description}
          </Typography>
        </Box>
      </Box>
    </Box>
  );
};

const DocumentosCiec = () => {
  const theme = useTheme();
  const navigate = useNavigate();
  const dispatch = useAppDispatch();
  const isMobileOrTablet = useMediaQuery(theme.breakpoints.down("md"));
  const { numberOfSteps, currentStep } = useTofuSteps(SCREEN_STATUS);
  const { nextStatus: naiveNextStatus } = useStatusConfig(SCREEN_STATUS);
  const { nextStatus: afterCiecStatus } = useStatusConfig(STATUS_CATALOG.consulta_ciec.code);
  const nextStatus = naiveNextStatus?.code === STATUS_CATALOG.consulta_ciec.code ? afterCiecStatus : naiveNextStatus;

  const applicationData = useSelector((state: RootState) => state.register.application);
  const personalData = useSelector((state: RootState) => state.register.personal_data);
  const accountUser = useSelector((state: RootState) => state.register.account_user);
  const brand = useSelector((state: RootState) => state.brand);
  const companyData = useSelector((state: RootState) => state.register.company);

  const [getDocumentsByAppId] = useLazyGetDocumentAppIdQuery();
  const [triggerPostNextStage] = usePostValidateNextStageMutation();
  const [getTabsConfig] = useLazyGetTabsConfigQuery();
  const [ciecValidation] = useCiecValidatorMutation();

  // bank account mutations / queries
  const [getTaxSystems] = useLazyGetTaxSystemsQuery();
  const [getBankAccounts] = useLazyGetBankAccountsQuery();
  const [triggerPostBankAccount] = usePostBankAccountsMutation();
  const [triggerPatchDocument] = usePatchDocumentMutation();
  const [triggerPostBankAccountStatement] = usePostBankAccountStatementMutation();
  const [triggerDeleteBankAccountStatement] = useDeleteBankAccountStatementMutation();
  const [deleteBankAccount] = useDeleteBankAccountMutation();
  const [triggerGetBanks] = useLazyGetBanksByCodeQuery();
  const [putApplicationCompleted] = usePutApplicationCompletedMutation();

  const [isLoading, setIsLoading] = useState(false);
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [docsFinancieros, setDocsFinancieros] = useState<BofuTabDocument[]>([]);
  const [docsActividad, setDocsActividad] = useState<BofuTabDocument[]>([]);
  const [selectedType, setSelectedType] = useState<DocumentType | null>(null);

  // bank account state
  const [isDeleting, setIsDeleting] = useState(false);
  const [loadingBankStatement, setLoadingBankStatement] = useState(false);
  const [accountIndexToDelete, setAccountIndexToDelete] = useState<number | undefined>(undefined);
  const [bankId, setBankId] = useState("");
  const [bankName, setBankName] = useState("");
  const [totalStatements, setTotalStatements] = useState(DEFAULT_NUMBER_BANK_STATEMENTS);
  const [requiredStatements, setRequiredStatements] = useState(DEFAULT_NUMBER_BANK_STATEMENTS);
  const isCreatingInitialAccount = useRef(false);

  const { expedienteDocuments, setExpedienteDocuments, handleUpdateDocument, handleUpdateDocumentMultiple } =
    useExpedienteDocuments();

  const hasBothDocumentTypes = docsFinancieros.length > 0 && docsActividad.length > 0;
  const showSelector = !isLoading && selectedType === null && hasBothDocumentTypes;

  // bank account formik (solo para validación; los datos se persisten en cada upload)
  const emptyBankAccount: BankAccountFormItem = createDefaultBankAccount(totalStatements, bankId, bankName);

  const validationSchema = useMemo(
    () =>
      yup.object().shape({
        accounts: yup.array().of(
          yup.object().shape({
            statements: yup
              .array()
              .of(
                yup.object().shape({
                  document_id: yup
                    .string()
                    .nullable()
                    .test("required-if-min", "Documento requerido.", function (value, params) {
                      return getBankStatementIndex(params.path) < requiredStatements ? Boolean(value) : true;
                    }),
                  document_order: yup.number().required().min(1).max(totalStatements),
                }),
              )
              .min(1, "Debes cargar al menos un documento."),
          }),
        ),
      }),
    [totalStatements, requiredStatements],
  );

  const bankFormik = useFormik({
    initialValues: {
      accounts: [{ ...emptyBankAccount }],
    },
    validationSchema,
    onSubmit: () => {},
    enableReinitialize: false,
    validateOnMount: true,
  });

  useEffect(() => {
    const needsResize = bankFormik.values.accounts.some(
      (account) => (account?.statements?.length || 0) !== totalStatements,
    );
    if (!needsResize) return;

    const resizedAccounts: BankAccountFormItem[] = bankFormik.values.accounts.map((account) => ({
      ...account,
      statements: normalizeStatements(account.statements, totalStatements),
    }));

    bankFormik.setFieldValue("accounts", resizedAccounts, false);
  }, [totalStatements]);

  const handleSubmit = async (event: FormEvent) => {
    event.preventDefault();

    try {
      if (!nextStatus) {
        throw new Error("No se encontró el siguiente status");
      }

      setIsSubmitting(true);

      const payload_ciec: CiecValidatorRequest = {
        rfc: companyData.rfc || "",
        ciec: "",
        application_code: applicationData.company_application_code,
        client_details_id: String(applicationData.client_details_id),
        loan_application_conditions_id: String(applicationData.application_condition_id),
        loan_application_id: String(applicationData.id),
        requested_amount: Number(applicationData.loan_amount),
        requested_term: Number(applicationData.loan_term),
        validate_ciec: false,
        company_id: brand?.id ?? "",
        connection_is_mobile_app: true,
      };

      if (
        applicationData.tax_system_type === TAX_SYSTEM_TYPE.PF.code ||
        applicationData.tax_system_type === TAX_SYSTEM_TYPE.PFAE.code
      ) {
        await ciecValidation(payload_ciec).unwrap();
      }

      await triggerPostNextStage({
        applicationId: applicationData.id,
        statusCode: nextStatus.code,
        username: `TOFU - ${accountUser.email}`,
      }).unwrap();

      await putApplicationCompleted({
        id_sol: applicationData.id,
        body: { bank_information_status: "COMPLETO" },
      }).unwrap();

      dispatch(setLoanStatus(nextStatus.code));
      navigate(nextStatus.url);
    } catch (error) {
      const apiError = error as ApiError;
      Sentry.captureException(error, {
        extra: {
          component: "DocumentosCiec-submit",
        },
      });
      dispatch(setModalErrorVisible({ open: true, error: apiError }));
    } finally {
      setIsSubmitting(false);
    }
  };

  const applyDocumentType = (type: DocumentType, financieros: BofuTabDocument[], actividad: BofuTabDocument[]) => {
    setSelectedType(type);
    setExpedienteDocuments(type === "financieros" ? financieros : actividad);
  };

  const handleSelectDocumentType = (type: DocumentType) => {
    applyDocumentType(type, docsFinancieros, docsActividad);
  };

  const handleChangeDocumentType = () => {
    if (selectedType === "financieros") {
      setDocsFinancieros(expedienteDocuments);
    } else if (selectedType === "actividad") {
      setDocsActividad(expedienteDocuments);
    }

    setSelectedType(null);
    setExpedienteDocuments([]);
  };

  const createInitialAccounts = async (index = 0, bankIdOverride?: string) => {
    try {
      if (isCreatingInitialAccount.current) return;
      isCreatingInitialAccount.current = true;

      let effectiveBankId = bankIdOverride ?? bankId;
      if (!effectiveBankId) {
        const bank_info = await triggerGetBanks(BANKCODE).unwrap();
        effectiveBankId = bank_info?.id || "";
        setBankId(effectiveBankId);
        setBankName(bank_info?.name || "");
      }

      if (!effectiveBankId) {
        dispatch(
          setModalErrorVisible({
            open: true,
            error: { message: "No se pudo obtener el banco para crear la cuenta inicial." } as ApiError,
          }),
        );
        return;
      }

      const payload: PayloadBankAccount = {
        loan_application_id: String(applicationData.id),
        client_details_id: personalData.id,
        account: 0,
        account_holder: "",
        person_id: null,
        clabe: "000000000000000000",
        bank_id: effectiveBankId as UUID,
        documents: [],
      };
      const response = await triggerPostBankAccount({
        solicitudId: applicationData.id,
        body: payload,
      }).unwrap();

      bankFormik.setFieldValue(`accounts[${index}].id`, response.id);
    } catch (error) {
      const api_error = error as ApiError;
      Sentry.captureException(error, {
        extra: {
          component: "DocumentosCiec-createInitialAccounts",
        },
      });
      dispatch(setModalErrorVisible({ open: true, error: api_error }));
    } finally {
      isCreatingInitialAccount.current = false;
    }
  };

  const createAccounts = async (index = 0, bankIdOverride?: string) => {
    try {
      setIsLoading(true);

      let effectiveBankId = bankIdOverride ?? bankId;
      if (!effectiveBankId) {
        const bank_info = await triggerGetBanks(BANKCODE).unwrap();
        effectiveBankId = bank_info?.id || "";
        setBankId(effectiveBankId);
        setBankName(bank_info?.name || "");
      }

      if (!effectiveBankId) {
        dispatch(
          setModalErrorVisible({
            open: true,
            error: { message: "No se pudo obtener el banco para crear la cuenta." } as ApiError,
          }),
        );
        return;
      }

      const payload: PayloadBankAccount = {
        loan_application_id: String(applicationData.id),
        client_details_id: personalData.id,
        account: 0,
        account_holder: "",
        person_id: null,
        clabe: `000000000000000000${index + 1}`,
        bank_id: effectiveBankId as UUID,
        documents: [],
      };
      const response = await triggerPostBankAccount({
        solicitudId: applicationData.id,
        body: payload,
      }).unwrap();

      bankFormik.setFieldValue(`accounts[${index}].id`, response.id);
    } catch (error) {
      const api_error = error as ApiError;
      Sentry.captureException(error, {
        extra: {
          component: "DocumentosCiec-createAccounts",
        },
      });
      dispatch(setModalErrorVisible({ open: true, error: api_error }));
    } finally {
      setIsLoading(false);
    }
  };

  const getInitialData = async () => {
    try {
      setIsLoading(true);

      const [documents, tabsConfig, tax_systems, list_bank_accounts, bank_info] = await Promise.all([
        getDocumentsByAppId({
          loanApplicationId: applicationData.id,
          clientDetailsId: personalData.id,
          status: true,
        }).unwrap(),
        getTabsConfig({ taxcode: applicationData.type }).unwrap(),
        getTaxSystems(applicationData.type).unwrap(),
        getBankAccounts(applicationData.id).unwrap(),
        triggerGetBanks(BANKCODE).unwrap(),
      ]);

      // docs expediente
      const docs_account = documents.filter((doc) => doc.client_details_id === applicationData.client_details_id);
      const clientDetailsId = applicationData.client_details_id as UUID;

      const tab_documentos_financieros = tabsConfig.find(
        (tab) => tab.tabs_bofu.code === BOFU_TABS_CONFIG.tab_documentos_sat.code,
      );
      const tab_documentos_actividad = tabsConfig.find(
        (tab) => tab.tabs_bofu.code === BOFU_TABS_CONFIG.tab_documentos_actividad.code,
      );

      const financieros = mapTabDocuments({
        tab: tab_documentos_financieros,
        docsAccount: docs_account,
        clientDetailsId,
      });
      const actividad = mapTabDocuments({
        tab: tab_documentos_actividad,
        docsAccount: docs_account,
        clientDetailsId,
      });

      setDocsFinancieros(financieros);
      setDocsActividad(actividad);

      if (financieros.length > 0 && actividad.length === 0) {
        applyDocumentType("financieros", financieros, actividad);
      } else if (actividad.length > 0 && financieros.length === 0) {
        applyDocumentType("actividad", financieros, actividad);
      } else {
        setSelectedType(null);
        setExpedienteDocuments([]);
      }

      // banco y config de estados de cuenta
      const { total_items, required_items } = getBankInformationConfig(
        tax_systems?.config_extra?.bank_information_config?.company,
      );
      setTotalStatements(total_items);
      setRequiredStatements(required_items);

      const resolvedBankId = bank_info?.id || "";
      const resolvedBankName = bank_info?.name || "";
      setBankId(resolvedBankId);
      setBankName(resolvedBankName);

      const { company_accounts } = identifyBankAccounts({
        bank_accounts: list_bank_accounts,
        company: personalData,
      });

      const accounts: BankAccountFormItem[] = [];
      const num_statements_required = total_items;

      for (let index = 0; index < company_accounts.length; index++) {
        const account = company_accounts[index];

        const new_account: BankAccountFormItem = {
          id: account.id,
          clabe: account.clabe || "",
          bank_id: account?.bank_id || resolvedBankId,
          bank_name: account?.bank_name || resolvedBankName,
          statements: normalizeStatements(undefined, num_statements_required),
        };

        for (let statementIndex = 0; statementIndex < num_statements_required; statementIndex++) {
          const account_document = account.documents[statementIndex];
          if (account_document) {
            new_account.statements[statementIndex] = {
              bank_statement_document_id: account_document.bank_statement_document_id,
              document_id: account_document.document_id,
              document_order: account_document.document_order || statementIndex + 1,
              document_value: documents.find((item) => item.id === account_document.document_id),
            };
          }
        }

        accounts.push(new_account);
      }

      if (accounts.length) {
        bankFormik.setFieldValue("accounts", accounts);
      } else {
        bankFormik.setFieldValue(
          "accounts",
          bankFormik.values.accounts.map((account) => ({
            ...account,
            bank_id: account.bank_id || resolvedBankId,
            bank_name: account.bank_name || resolvedBankName,
          })),
          false,
        );
        await createInitialAccounts(0, resolvedBankId);
      }
    } catch (error) {
      const apiError = error as ApiError;
      Sentry.captureException(error, {
        extra: {
          component: "DocumentosCiec-getInitialData",
        },
      });
      dispatch(setModalErrorVisible({ open: true, error: apiError }));
    } finally {
      setIsLoading(false);
    }
  };

  useEffect(() => {
    getInitialData();
  }, []);

  const handleAddAccount = async () => {
    if (bankFormik.values.accounts.length >= 3) return;

    let resolvedBankId = bankId;
    let resolvedBankName = bankName;

    if (!resolvedBankId) {
      const bank_info = await triggerGetBanks(BANKCODE).unwrap();
      resolvedBankId = bank_info?.id || "";
      resolvedBankName = bank_info?.name || "";
      setBankId(resolvedBankId);
      setBankName(resolvedBankName);
    }

    if (!resolvedBankId) {
      dispatch(
        setModalErrorVisible({
          open: true,
          error: { message: "No se pudo obtener el banco para crear la cuenta." } as ApiError,
        }),
      );
      return;
    }

    bankFormik.setFieldValue("accounts", [
      ...bankFormik.values.accounts,
      createDefaultBankAccount(totalStatements, resolvedBankId, resolvedBankName),
    ]);
    await createAccounts(bankFormik.values.accounts.length, resolvedBankId);
  };

  const handleUploadBankStatement = async (
    newUpload: DocumentResponse | undefined,
    accountIndex: number,
    statementIndex: number,
  ) => {
    try {
      setLoadingBankStatement(true);

      const copy_accounts: BankAccountFormItem[] = [...bankFormik.values.accounts];
      const account: BankAccountFormItem = copy_accounts[accountIndex];
      const statement = account.statements[statementIndex];

      if (newUpload) {
        if (account.id) {
          const response = await triggerPostBankAccountStatement({
            solicitudId: applicationData.id,
            accountId: account.id,
            body: {
              loan_application_bank_account_id: account.id,
              document_id: newUpload.id,
              document_order: statementIndex + 1,
            },
          }).unwrap();

          copy_accounts[accountIndex].statements[statementIndex] = {
            bank_statement_document_id: response.id,
            document_id: newUpload.id,
            document_order: statementIndex + 1,
            document_value: newUpload,
          };

          bankFormik.setFieldValue("accounts", copy_accounts);
        }
      } else {
        if (account.id && statement?.bank_statement_document_id) {
          await triggerDeleteBankAccountStatement({
            solicitudId: applicationData.id,
            accountId: account.id,
            documentId: statement.bank_statement_document_id,
          }).unwrap();

          copy_accounts[accountIndex].statements[statementIndex] = {
            bank_statement_document_id: undefined,
            document_id: "",
            document_order: statementIndex + 1,
            document_value: undefined,
          };

          bankFormik.setFieldValue("accounts", copy_accounts);
        }
      }
    } catch (error) {
      Sentry.captureException(error, {
        extra: {
          component: "DocumentosCiec-handleUploadBankStatement",
        },
      });
    } finally {
      setLoadingBankStatement(false);
    }
  };

  const handleOpenModalDelete = (index: number) => {
    setAccountIndexToDelete(index);
  };

  const handleOnCloseModalDelete = () => {
    setAccountIndexToDelete(undefined);
  };

  const handleConfirmDelete = async () => {
    try {
      setIsDeleting(true);
      if (accountIndexToDelete === undefined) return;

      const copy_accounts: BankAccountFormItem[] = [...bankFormik.values.accounts];
      const account_to_delete = copy_accounts[accountIndexToDelete];

      if (!account_to_delete || !account_to_delete.id) {
        const new_accounts = copy_accounts.filter((_, index) => index !== accountIndexToDelete);
        bankFormik.setFieldValue("accounts", new_accounts);
        setAccountIndexToDelete(undefined);
        return;
      }

      const documents_validated = account_to_delete.statements.filter(
        (item) => item?.document_value?.validated === true,
      );

      if (documents_validated.length > 0) {
        console.error("no se puede eliminar la cuenta bancaria porque hay documentos validados relacionados.");
        return;
      }

      for (let index = 0; index < account_to_delete.statements.length; index++) {
        const bank_statement = account_to_delete.statements[index];
        if (bank_statement && bank_statement.document_id) {
          await triggerPatchDocument({
            documentId: bank_statement.document_id as `${string}-${string}-${string}-${string}-${string}`,
            body: { status: false },
          }).unwrap();
        }
      }

      await deleteBankAccount({
        solicitudId: applicationData.id,
        accountId: account_to_delete.id,
      }).unwrap();

      const new_accounts = copy_accounts.filter((_, index) => index !== accountIndexToDelete);
      bankFormik.setFieldValue("accounts", new_accounts);
      handleOnCloseModalDelete();
    } catch (error) {
      console.error("Ha ocurrido un error al eliminar la cuenta bancaria", error);
      Sentry.captureException(error, {
        extra: {
          component: "DocumentosCiec-handleConfirmDelete",
        },
      });
    } finally {
      setIsDeleting(false);
    }
  };

  const [canContinue, setCanContinue] = useState(false);

  useEffect(() => {
    setCanContinue(
      !isLoading &&
        !isSubmitting &&
        !showSelector &&
        validateRequiredDocumentsAreUploaded(expedienteDocuments) &&
        bankFormik.isValid,
    );
  }, [expedienteDocuments, isLoading, isSubmitting, showSelector, bankFormik.isValid]);

  const activeTabCode =
    selectedType === "actividad"
      ? BOFU_TABS_CONFIG.tab_documentos_actividad.code
      : BOFU_TABS_CONFIG.tab_documentos_sat.code;

  const isBankActionDisabled = isLoading || isDeleting || loadingBankStatement || isSubmitting;

  return (
    <Grid container height="100%">
      <Grid item xs={12} md={8} mt={2}>
        <InactivityHandler />

        <Box
          sx={{
            mt: 2,
            mx: { xs: 2, md: 4 },
            display: "flex",
            flexDirection: "column",
            alignItems: "center",
          }}
        >
          <Box maxWidth="sm" width="100%">
            <Typography variant="overline" fontWeight={700} letterSpacing={1.6} color="primary.main">
              EMPRESA · DOCUMENTOS
            </Typography>

            <Typography variant="tofu_title" textAlign="left" color="primary.dark">
              Sube los documentos de tu empresa
            </Typography>

            <Typography variant={isMobileOrTablet ? "body2" : "body1"} mt={1} color="primary.dark">
              {showSelector
                ? "Elige el tipo de documentos que vas a cargar. Solo necesitas subir uno de los dos."
                : "Necesitamos estos documentos para evaluar tu solicitud de crédito."}
            </Typography>
          </Box>

          <Container maxWidth="sm" sx={{ mt: isMobileOrTablet ? 2 : 3, px: "0 !important" }}>
            <ProgressBar totalSteps={numberOfSteps} currentStep={currentStep} />
          </Container>

          <Container maxWidth="sm" sx={{ mt: 3, mb: 2, px: "0 !important" }}>
            {showSelector ? (
              <Box display="flex" flexDirection="column" gap={2}>
                {DOCUMENT_TYPE_OPTIONS.map((option) => (
                  <DocumentTypeOptionCard
                    key={option.value}
                    title={option.title}
                    description={option.description}
                    icon={option.icon}
                    isSelected={false}
                    isMobileOrTablet={isMobileOrTablet}
                    onSelect={() => handleSelectDocumentType(option.value)}
                  />
                ))}
              </Box>
            ) : (
              <form onSubmit={handleSubmit}>
                {hasBothDocumentTypes && selectedType && (
                  <Link
                    component="button"
                    type="button"
                    variant="body1"
                    onClick={handleChangeDocumentType}
                    sx={{ display: "block", mb: 2, textAlign: "left" }}
                  >
                    Elegir otro tipo de documentos
                  </Link>
                )}

                <DocumentsTabBofu
                  inputPrefix={`cliente`}
                  tabCode={activeTabCode}
                  documents={expedienteDocuments}
                  onChange={handleUpdateDocument}
                  onChangeMultiple={handleUpdateDocumentMultiple}
                  RFC={companyData.rfc}
                />

                <Box mt={2}>
                  <Typography
                    textAlign="left"
                    color="primary.dark"
                    variant={isMobileOrTablet ? "body2" : "body1"}
                    fontWeight={300}
                    mb={3}
                  >
                    Ingresa los últimos {totalStatements} estados de cuenta bancarios de la cuenta en dónde concentras
                    el mayor ingreso de tu negocio. Completa al menos {requiredStatements}.
                  </Typography>

                  {bankFormik.values.accounts.map((account: BankAccountFormItem, index: number) => (
                    <Box key={index} mt={index > 0 ? 4 : 0}>
                      <Typography
                        textAlign="center"
                        variant={isMobileOrTablet ? "body2" : "body1"}
                        fontWeight={700}
                        color="primary.dark"
                      >
                        Cuenta Bancaria {index + 1}
                      </Typography>

                      {account.statements.map((statement, statementIndex) => (
                        <Box key={`bank_statement_${index}_${statementIndex}`}>
                          <CustomUploadFileV2
                            mt={2}
                            inputName={`bank_statement_${index}_${statementIndex}`}
                            uploadedFile={statement?.document_value}
                            setUploadedFile={(newUpload) => {
                              handleUploadBankStatement(newUpload, index, statementIndex);
                            }}
                            optionalName={`Estado de cuenta ${statementIndex + 1}`}
                            optionalDescription=""
                            metadata={{
                              fileCode: DOCUMENTS_CATALOG.estado_cuenta_bancario.code,
                              uploadType: "client",
                              clientDetailsId: personalData.id,
                            }}
                            disabled={isBankActionDisabled || !account.id}
                            required={statementIndex < requiredStatements}
                          />
                        </Box>
                      ))}

                      {index > 0 && (
                        <Typography
                          onClick={() => handleOpenModalDelete(index)}
                          color="primary.main"
                          fontSize="15px"
                          fontWeight={400}
                          mt={3}
                          sx={{
                            cursor:
                              isBankActionDisabled || bankFormik.values.accounts[index].id === undefined
                                ? "not-allowed"
                                : "pointer",
                          }}
                        >
                          - Eliminar cuenta bancaria
                        </Typography>
                      )}
                    </Box>
                  ))}

                  <Box
                    mt={3}
                    sx={{
                      display: "flex",
                      alignItems: "center",
                      gap: "8px",
                      backgroundColor: theme?.palette?.primary?.base,
                      padding: "10px 12px",
                      borderRadius: "8px",
                      paddingLeft: { xs: "12px", sm: "26px" },
                      paddingRight: { xs: "12px", sm: "106px" },
                    }}
                  >
                    <WarningAmberIcon sx={{ color: theme?.palette?.primary?.light, fontSize: 40 }} />
                    <Typography
                      sx={{ fontSize: "14px", color: theme?.palette?.primary?.light, lineHeight: 1.4 }}
                      textAlign="justify"
                      pl={2}
                    >
                      Si la cuenta refleja menos del 60% de los ingresos operativos, agrega otra cuenta bancaria hasta
                      llegar a dicho porcentaje.
                    </Typography>
                  </Box>

                  <Typography
                    onClick={handleAddAccount}
                    color="primary.main"
                    fontSize="15px"
                    fontWeight={400}
                    mt={3}
                    sx={{
                      cursor: bankFormik.values.accounts.length >= 3 ? "not-allowed" : "pointer",
                    }}
                  >
                    + Agregar otra cuenta bancaria
                  </Typography>
                </Box>

                <Container maxWidth="sm" sx={{ mb: 2, mt: 4, px: "0 !important" }}>
                  <Box display="flex" justifyContent="center">
                    <Button variant="default" color="primary" type="submit" disabled={!canContinue}>
                      Continuar
                    </Button>
                  </Box>
                </Container>
              </form>
            )}
          </Container>
        </Box>
      </Grid>

      {!isMobileOrTablet && (
        <Grid item xs={12} md={4}>
          <CustomRightImage />
        </Grid>
      )}

      <CustomDialog
        colorClose={theme?.palette?.primary?.light}
        open={accountIndexToDelete !== undefined}
        onClose={handleOnCloseModalDelete}
        itemTitle={<></>}
        itemContent={
          <Box display="flex" flexDirection="column" justifyContent="center" alignItems="center">
            <Typography
              textAlign="center"
              color="primary.dark"
              fontWeight={600}
              variant={isMobileOrTablet ? "h5" : "h6"}
            >
              ¿Estás seguro de eliminar la cuenta bancaria?
            </Typography>
            <Box display="flex" columnGap={2} justifyContent="center" mt={3}>
              <Button
                variant="blue_outlined"
                sx={{ py: 0, px: 1, width: "110px" }}
                onClick={handleOnCloseModalDelete}
                disabled={isDeleting}
              >
                Regresar
              </Button>
              <Button
                variant="default"
                sx={{ width: "110px" }}
                onClick={() => handleConfirmDelete()}
                disabled={isDeleting}
              >
                Eliminar
              </Button>
            </Box>
          </Box>
        }
      />
    </Grid>
  );
};

export default DocumentosCiec;
